import { ReactNode, ComponentType, ComponentProps } from 'react'
import { TbArrowBackUp } from 'react-icons/tb'

import { Link } from 'components/Link'
import { NextLink } from 'components/Link/NextLink'
import {
  Box,
  BoxProps,
  HstackProps,
  VStack,
  VstackProps,
} from 'styled-system/jsx'
import { Heading, HeadingProps } from 'ui-kit/heading'
import { Icon, IconProps } from 'ui-kit/icon'
import { Text } from 'ui-kit/text'

function SidebarRoot({ children, ...props }: VstackProps) {
  return (
    <VStack
      alignItems='stretch'
      gap='0'
      flex='0 0 15rem'
      width='15rem'
      position='relative'
      height='100vh'
      backgroundColor='bg.canvas'
      borderInlineEndWidth='1'
      borderInlineEndColor='border.subtle'
      borderInlineEndStyle='solid'
      {...props}
    >
      {children}
    </VStack>
  )
}

function SidebarBlock({ children, ...props }: VstackProps) {
  return (
    <VStack
      alignItems='stretch'
      gap='1'
      paddingInline='2'
      paddingBlock='2'
      {...props}
    >
      {children}
    </VStack>
  )
}

function SidebarHeader({ children, ...props }: HeadingProps) {
  return (
    <Heading
      as='h2'
      textStyle='lg'
      lineHeight='none'
      marginBlockStart='1.5'
      marginBlockEnd='1'
      paddingInlineStart='1'
      {...props}
    >
      {children}
    </Heading>
  )
}

function SidebarTitle({ children, ...props }: HeadingProps) {
  return (
    <Heading
      as='h3'
      textStyle='sm'
      lineHeight='none'
      fontWeight='medium'
      color='fg.muted'
      marginBlockStart={{
        base: '4',
        _first: '0',
      }}
      marginBlockEnd='0'
      paddingInlineStart='1'
      paddingBlock='1.5'
      {...props}
    >
      {children}
    </Heading>
  )
}

function SidebarLinks({ children, ...props }: HstackProps) {
  return (
    <VStack gap='0.5' alignItems='stretch' {...props}>
      {children}
    </VStack>
  )
}

function createSidebarLink(LinkComponent: ComponentType<any>) {
  return function SidebarLink({
    children,
    icon,
    stats,
    rightSlot,
    ...props
  }: ComponentProps<typeof LinkComponent> & {
    icon?: ReactNode
    stats?: ReactNode
    rightSlot?: ReactNode
  }) {
    return (
      <LinkComponent
        display='flex'
        alignItems='center'
        gap='1'
        paddingInline='2'
        paddingBlock='1.5'
        borderRadius='l2'
        backgroundColor={{
          _hover: 'bg.muted/50',
          _active: 'bg.muted',
        }}
        textDecoration={{
          base: 'none',
          _hover: 'none',
        }}
        {...props}
      >
        <SidebarIcon>{icon}</SidebarIcon>
        {children}
        <SidebarStats>{stats}</SidebarStats>
        <SidebarLinkSlot marginInlineStart='auto'>{rightSlot}</SidebarLinkSlot>
      </LinkComponent>
    )
  }
}

const SidebarLink = createSidebarLink(Link)
const NextSidebarLink = createSidebarLink(NextLink)

function SidebarLinkSlot({ children, ...props }: BoxProps) {
  if (!children) {
    return null
  }
  return (
    <Box display='flex' {...props}>
      {children}
    </Box>
  )
}

function SidebarIcon({ children, ...props }: IconProps) {
  if (!children) {
    return null
  }
  return (
    <Icon size='sm' {...props}>
      {children}
    </Icon>
  )
}

function SidebarStats({ children, ...props }: BoxProps) {
  if (children === undefined || children === null) {
    return null
  }
  return (
    <Text
      as='span'
      display='inline-flex'
      minWidth='4'
      alignItems='center'
      justifyContent='center'
      fontSize='2xs'
      borderRadius={'l3'}
      paddingInline='1'
      paddingBlock='0.5'
      marginInlineStart='1'
      color='fg.muted'
      backgroundColor='bg.subtle'
      {...props}
    >
      {children}
    </Text>
  )
}

function createBackToLink(LinkComponent: ComponentType<any>) {
  return function SidebarBackToLink({
    children,
    ...props
  }: ComponentProps<typeof LinkComponent>) {
    return (
      <LinkComponent color='fg.muted' marginBlockEnd='2' {...props}>
        <Icon size='sm'>
          <TbArrowBackUp />
        </Icon>
        {children}
      </LinkComponent>
    )
  }
}

const SidebarBackToLink = createBackToLink(Link)
const NextSidebarBackToLink = createBackToLink(NextLink)

const SidebarMenu = {
  Root: SidebarRoot,
  Block: SidebarBlock,

  Header: SidebarHeader,
  Title: SidebarTitle,

  Links: SidebarLinks,
  Link: SidebarLink,
  BackToLink: SidebarBackToLink,

  NextLink: NextSidebarLink,
  NextBackToLink: NextSidebarBackToLink,
}

export { SidebarMenu }
